import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const PREFIX_TIMEOUT_MS = 1500;

const G_ROUTES: Readonly<Record<string, string>> = {
  o: "/orders",
  r: "/recipes",
  p: "/pantry",
  k: "/packaging",
  b: "/baking",
  s: "/settings",
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useGlobalShortcuts(): void {
  const navigate = useNavigate();

  useEffect(() => {
    let awaitingG = false;
    let resetTimer: ReturnType<typeof setTimeout> | null = null;

    const clearPrefix = (): void => {
      awaitingG = false;
      if (resetTimer !== null) {
        clearTimeout(resetTimer);
        resetTimer = null;
      }
    };

    const armPrefix = (): void => {
      awaitingG = true;
      if (resetTimer !== null) clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        awaitingG = false;
        resetTimer = null;
      }, PREFIX_TIMEOUT_MS);
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (
        isEditableTarget(event.target) ||
        (event.target instanceof HTMLElement &&
          event.target.closest('[role="dialog"], [role="alertdialog"]'))
      ) {
        clearPrefix();
        return;
      }

      const key = event.key;

      if (key === "Escape") {
        clearPrefix();
        document.dispatchEvent(new CustomEvent("bakery:dismiss-modal"));
        return;
      }

      if (awaitingG) {
        const lower = key.toLowerCase();
        const path = G_ROUTES[lower];
        clearPrefix();
        if (path !== undefined) {
          event.preventDefault();
          void navigate(path);
        }
        return;
      }

      if (key === "?" && event.shiftKey) {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent("bakery:show-shortcuts-help"));
        return;
      }

      if (key === "g" || key === "G") {
        event.preventDefault();
        armPrefix();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return (): void => {
      document.removeEventListener("keydown", handleKeyDown);
      if (resetTimer !== null) clearTimeout(resetTimer);
    };
  }, [navigate]);
}
